import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import type { AdminKeysSummary } from '@traceio-org/platform-contracts';
import { isAbortError } from './api';
import { plural } from './format';
import { useAdmin } from './context';

interface KeysSummaryValue {
  /** The latest counts, or null before the first answer, when the read failed, or when Keys is not connected. */
  summary: AdminKeysSummary | null;
  /** Read the counts again (after a change on the Keys page). */
  refresh: () => void;
}

const KeysSummaryContext = createContext<KeysSummaryValue>({ summary: null, refresh: () => {} });

/** The keys that need someone now: expired, or due within their reminder window. The badge and Health's line. */
export function keysNeedingAttention(summary: AdminKeysSummary | null): number {
  if (!summary?.connected) return 0;
  return summary.counts.expired + summary.counts.due;
}

/**
 * The words of that count: "1 key expired or due", "3 keys expired or due". Due means within each key's own reminder
 * window (up to 90 days), so never "this month".
 */
export function keysAttentionWords(count: number): string {
  return `${plural(count, 'key', 'keys')} expired or due`;
}

/**
 * The Keys counts for the navigation badge and Health's line, read once per page Trace Admin shows (a navigation reads
 * them again, so the badge follows a change made a moment ago). platform-api answers them from its two minute
 * inventory and does not audit them (they name no key). A failed read shows no badge rather than an error: the Keys
 * page itself says what went wrong.
 */
export function KeysSummaryProvider({ children }: { children: ReactNode }) {
  const { api } = useAdmin();
  const { pathname } = useLocation();
  const [summary, setSummary] = useState<AdminKeysSummary | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    api.readKeysSummary(controller.signal).then(
      (answer) => {
        if (!controller.signal.aborted) setSummary(answer);
      },
      (error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        setSummary(null);
      },
    );
    return () => controller.abort();
  }, [api, pathname, attempt]);
  const refresh = useCallback(() => setAttempt((n) => n + 1), []);
  return <KeysSummaryContext.Provider value={{ summary, refresh }}>{children}</KeysSummaryContext.Provider>;
}

export function useKeysSummary(): KeysSummaryValue {
  return useContext(KeysSummaryContext);
}
